import { supabase } from '@/lib/supabase/client';
import {
  mergeLocationReadinessGates,
  resolveCompletedAtForStatus,
  type ReadinessGateRow,
} from '@/lib/customer-ops/readiness-gates';
import type {
  LocationReadinessGate,
  LocationReadinessGateUpdate,
  ReadinessGateId,
  ReadinessGateStatus,
} from '@/types/readiness-gates';

function nullableText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function normalizeStatus(status: ReadinessGateStatus | undefined): ReadinessGateStatus {
  return status ?? 'not_started';
}

export const locationReadinessGatesService = {
  async listByLocationId(locationId: string): Promise<LocationReadinessGate[]> {
    try {
      const { data, error } = await supabase
        .from('location_readiness_gates')
        .select('*')
        .eq('location_id', locationId)
        .order('gate_id', { ascending: true });

      if (error) {
        console.error('Error fetching location readiness gates:', error);
        return mergeLocationReadinessGates(locationId, []);
      }

      return mergeLocationReadinessGates(locationId, (data ?? []) as ReadinessGateRow[]);
    } catch (error) {
      console.error('Error fetching location readiness gates:', error);
      return mergeLocationReadinessGates(locationId, []);
    }
  },

  async listAllGrouped(): Promise<Map<string, LocationReadinessGate[]>> {
    try {
      const { data, error } = await supabase
        .from('location_readiness_gates')
        .select('*')
        .order('location_id', { ascending: true })
        .order('gate_id', { ascending: true });

      if (error) {
        console.error('Error fetching all readiness gates:', error);
        return new Map();
      }

      const rowsByLocation = new Map<string, ReadinessGateRow[]>();
      for (const row of (data ?? []) as ReadinessGateRow[]) {
        const existing = rowsByLocation.get(row.location_id) ?? [];
        existing.push(row);
        rowsByLocation.set(row.location_id, existing);
      }

      const grouped = new Map<string, LocationReadinessGate[]>();
      for (const [locationId, rows] of rowsByLocation) {
        grouped.set(locationId, mergeLocationReadinessGates(locationId, rows));
      }
      return grouped;
    } catch (error) {
      console.error('Error fetching all readiness gates:', error);
      return new Map();
    }
  },

  async upsertGate(
    locationId: string,
    gateId: ReadinessGateId,
    data: LocationReadinessGateUpdate
  ): Promise<LocationReadinessGate> {
    const existingRows = await this.listByLocationId(locationId);
    const existing = existingRows.find((gate) => gate.gate_id === gateId);
    if (!existing) {
      throw new Error(`Unknown readiness gate: ${gateId}`);
    }

    const nextStatus = normalizeStatus(data.status ?? existing.status);
    const nextOwner =
      data.owner !== undefined ? nullableText(data.owner) : nullableText(existing.owner);
    const nextDueDate =
      data.due_date !== undefined ? nullableText(data.due_date) : existing.due_date;
    const nextNotes = data.notes !== undefined ? data.notes : existing.notes;
    const nextCompletedAt = resolveCompletedAtForStatus(nextStatus, existing.completed_at);

    const payload = {
      location_id: locationId,
      gate_id: gateId,
      status: nextStatus,
      owner: nextOwner,
      due_date: nextDueDate,
      completed_at: nextCompletedAt,
      notes: nextNotes,
    };

    const { data: saved, error } = await supabase
      .from('location_readiness_gates')
      .upsert(payload, { onConflict: 'location_id,gate_id' })
      .select()
      .single();

    if (error) {
      console.error('Error upserting location readiness gate:', error);
      throw new Error(`Failed to save readiness gate ${gateId}: ${error.message}`);
    }

    return mergeLocationReadinessGates(locationId, [saved as ReadinessGateRow]).find(
      (gate) => gate.gate_id === gateId
    )!;
  },
};
